/**
 * Calendar days, as plain `YYYY-MM-DD` strings.
 *
 * A day on the calendar is a label, not an instant, so everything here works on the
 * string and does its arithmetic in UTC. Going through a local-time `Date` would move a
 * day by one around a daylight-saving change, which is exactly the kind of fault nobody
 * notices until a deadline lands on the wrong square.
 */

import type { CalendarItem } from '@/lib/api';
import { isoDate } from '@/lib/format';

const DAY_MS = 86_400_000;

const pad = (n: number) => String(n).padStart(2, '0');

const toDate = (day: string) => new Date(`${day}T00:00:00Z`);
const toDay = (d: Date) => `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;

export function addDays(day: string, n: number): string {
  return toDay(new Date(toDate(day).getTime() + n * DAY_MS));
}

export function dayOfMonth(day: string): number {
  return Number(day.slice(8, 10));
}

export function monthOf(day: string): { year: number; month: number } {
  return { year: Number(day.slice(0, 4)), month: Number(day.slice(5, 7)) - 1 };
}

export function firstOfMonth(year: number, month: number): string {
  return `${year}-${pad(month + 1)}-01`;
}

/** The month before or after, wrapping the year. */
export function shiftMonth(year: number, month: number, delta: number): { year: number; month: number } {
  const index = year * 12 + month + delta;
  return { year: Math.floor(index / 12), month: ((index % 12) + 12) % 12 };
}

/**
 * The six weeks a month grid shows, Monday first: the month's own days plus the tail of
 * the previous month and the start of the next, so the first and last rows are not
 * half empty. Forty-two days, which is well inside the 93 the API allows.
 */
export function monthGrid(year: number, month: number): { from: string; to: string; days: string[] } {
  const first = firstOfMonth(year, month);
  const mondayOffset = (toDate(first).getUTCDay() + 6) % 7;
  const from = addDays(first, -mondayOffset);
  const days = Array.from({ length: 42 }, (_, i) => addDays(from, i));
  return { from, to: days[41]!, days };
}

/** First and last day an item covers, in Milan. Inclusive; a single-day item has them equal. */
export function itemSpan(item: CalendarItem): { first: string; last: string } {
  const first = isoDate(item.start);
  const last = item.end ? isoDate(item.end) : first;
  return { first, last: last < first ? first : last };
}

/** Items keyed by every day of the window they cover. */
export function itemsByDay(items: CalendarItem[], from: string, to: string): Map<string, CalendarItem[]> {
  const byDay = new Map<string, CalendarItem[]>();
  for (const item of items) {
    const span = itemSpan(item);
    let day = span.first < from ? from : span.first;
    const stop = span.last > to ? to : span.last;
    // The cap is only a guard against a malformed range turning into a very long loop.
    for (let guard = 0; day <= stop && guard < 100; guard += 1) {
      const list = byDay.get(day) ?? [];
      list.push(item);
      byDay.set(day, list);
      day = addDays(day, 1);
    }
  }
  for (const list of byDay.values()) list.sort(compareItems);
  return byDay;
}

const KIND_ORDER = { leave: 0, deadline: 1, appointment: 2 } as const;

/** All-day things first, then by start; ties by kind and title so the order is stable. */
export function compareItems(a: CalendarItem, b: CalendarItem): number {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  if (a.allDay) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.title.localeCompare(b.title, 'it');
  return a.start.localeCompare(b.start) || a.title.localeCompare(b.title, 'it');
}
